import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import apiClient from '../api/apiClient';
import { formatCurrency, toLocalDateStr } from '../utils/formatters';

// ── Helpers ─────────────────────────────────────────────────────────
const toDateStr = toLocalDateStr;

function getMonday(refDate) {
  const d = new Date(refDate + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() - ((day + 6) % 7));
  return toDateStr(d);
}

function formatDayHeader(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return { dayName: days[d.getDay()], dayNum: d.getDate() };
}

function formatWeekLabel(weekDays) {
  if (!weekDays || weekDays.length === 0) return '';
  const mon = new Date(weekDays[0] + 'T00:00:00');
  const fri = new Date(weekDays[4] + 'T00:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[mon.getMonth()]} ${mon.getDate()} – ${months[fri.getMonth()]} ${fri.getDate()}, ${fri.getFullYear()}`;
}

function fmtVal(v, unit) {
  if (v === null || v === undefined || v === 0) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (unit === 'currency') {
    if (Math.abs(n) >= 1000) return '$' + (n / 1000).toFixed(1) + 'k';
    return formatCurrency(n);
  }
  if (unit === 'percent') return n.toFixed(1) + '%';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function fmtFull(v, unit) {
  if (v === null || v === undefined || v === 0) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (unit === 'currency') return formatCurrency(n);
  if (unit === 'percent') return n.toFixed(1) + '%';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

// The default KPI sections were created with a "Walker" prefix for every brand (so Francis
// showed "Walker Membership" etc). Shown without it; the stored name is unchanged so budget
// links and colors keep working.
const displayDept = (d) => String(d || '').replace(/^Walker\s+/i, '');

// Department color map
const DEPT_COLORS = {
  'Walker Install Department': '#3b82f6',
  'Walker Comfort Advisors Team': '#8b5cf6',
  'Walker Leads': '#06b6d4',
  'Walker Service Department': '#f59e0b',
  'Demand': '#ef4444',
  'Walker Membership': '#10b981',
  'Electrical': '#f97316',
  'PLUMBING': '#6366f1',
  'Call Center': '#ec4899',
};

// ── Editable Cell ───────────────────────────────────────────────────
function EditCell({ value, onSave, unit, style: extraStyle }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  const start = () => { setVal(value || ''); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const n = Number(val);
    if (!isNaN(n) && n !== Number(value || 0)) onSave(n);
  };

  if (editing) {
    return <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)}
      onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ width: '55px', padding: '0.1rem 0.25rem', border: '1.5px solid #6366f1', borderRadius: '0.2rem', fontSize: '0.7rem', textAlign: 'right' }}
    />;
  }

  return (
    <div onClick={start} style={{ cursor: 'pointer', minWidth: '40px', textAlign: 'right', fontSize: '0.7rem', padding: '0.1rem 0.15rem', borderRadius: '0.2rem', ...extraStyle }} title="Click to edit">
      {fmtVal(value, unit)}
    </div>
  );
}

// ── Budget Line Picker (department -> line-name, sourced from real Budget-tab items) ──
function BudgetLinkPicker({ budgetItems, dept, lineName, onChange }) {
  const deptOptions = useMemo(() => [...new Set(budgetItems.map(bi => bi.department))].sort(), [budgetItems]);
  const lineOptions = useMemo(() => budgetItems.filter(bi => bi.department === dept).map(bi => bi.line_name), [budgetItems, dept]);
  return (
    <>
      <select value={dept || ''} onChange={e => onChange(e.target.value || null, null)} style={{ ...st.input, width: '160px' }}>
        <option value="">No budget link</option>
        {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      {dept && (
        <select value={lineName || ''} onChange={e => onChange(dept, e.target.value || null)} style={{ ...st.input, width: '160px' }}>
          <option value="">Select line item</option>
          {lineOptions.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      )}
    </>
  );
}

// ── Add KPI Form ────────────────────────────────────────────────────
function AddKpiForm({ onAdd, onCancel, departments, budgetItems, sourceOptions }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('number');
  const [source, setSource] = useState('manual');
  const [department, setDepartment] = useState(departments[0] || '');
  const [budgetDept, setBudgetDept] = useState(null);
  const [budgetLine, setBudgetLine] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd({ name: name.trim(), unit, source, department, budget_department: budgetDept, budget_line_name: budgetLine });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
      <select value={department} onChange={e => setDepartment(e.target.value)} style={{ ...st.input, width: '180px' }}>
        {departments.map(d => <option key={d} value={d}>{displayDept(d)}</option>)}
        <option value="">Other</option>
      </select>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="KPI name" style={st.input} autoFocus />
      <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...st.input, width: '100px' }}>
        <option value="number">Number</option>
        <option value="currency">Currency</option>
        <option value="percent">Percent</option>
      </select>
      <select value={source} onChange={e => setSource(e.target.value)} style={{ ...st.input, width: '220px' }}>
        {sourceOptions?.sources?.length ? (
          Object.entries(sourceOptions.sources.reduce((g, s) => { (g[s.group] = g[s.group] || []).push(s); return g; }, {})).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </optgroup>
          ))
        ) : (
          <>
            <option value="manual">Manual</option>
            <option value="auto_jobs">Auto: Jobs</option>
            <option value="auto_revenue">Auto: Revenue</option>
            <option value="auto_completed">Auto: Completed</option>
            <option value="auto_avg_ticket">Auto: Avg Ticket</option>
          </>
        )}
      </select>
      <BudgetLinkPicker budgetItems={budgetItems} dept={budgetDept} lineName={budgetLine} onChange={(d, l) => { setBudgetDept(d); setBudgetLine(l); }} />
      <button type="submit" disabled={saving} style={st.addBtn}>{saving ? '...' : 'Add'}</button>
      <button type="button" onClick={onCancel} style={st.cancelBtn}>Cancel</button>
    </form>
  );
}

// ── Edit Budget Link (inline, per existing KPI) ──────────────────────
function EditBudgetLink({ kpi, budgetItems, onSave, onClose }) {
  const [dept, setDept] = useState(kpi.budget_department || null);
  const [lineName, setLineName] = useState(kpi.budget_line_name || null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(kpi.id, { budget_department: dept, budget_line_name: lineName });
      onClose();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', padding: '0.4rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.375rem' }}>
      <BudgetLinkPicker budgetItems={budgetItems} dept={dept} lineName={lineName} onChange={(d, l) => { setDept(d); setLineName(l); }} />
      {/* A department needs a line item before saving - saving it half-picked made a broken link. */}
      <button onClick={handleSave} disabled={saving || (dept && !lineName)} title={dept && !lineName ? 'Pick a line item first' : undefined} style={{ ...st.addBtn, padding: '0.25rem 0.6rem', fontSize: '0.7rem', opacity: dept && !lineName ? 0.5 : 1 }}>{saving ? '...' : 'Save'}</button>
      <button onClick={onClose} style={{ ...st.cancelBtn, padding: '0.25rem 0.6rem', fontSize: '0.7rem' }}>Cancel</button>
    </div>
  );
}

// ── Edit Source (what this KPI's ACTUAL pulls from, + which business unit) ──
function EditSource({ kpi, options, onSave, onClose }) {
  const [source, setSource] = useState(kpi.source || 'manual');
  const [bu, setBu] = useState(kpi.source_key || '');
  const [saving, setSaving] = useState(false);
  const sources = options?.sources || [];
  const businessUnits = options?.businessUnits || [];
  const selected = sources.find(s => s.value === source);
  const groups = useMemo(() => {
    const g = {};
    for (const s of sources) (g[s.group] = g[s.group] || []).push(s);
    return g;
  }, [sources]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // '' clears a previously chosen business unit (null would leave it untouched server-side)
      await onSave(kpi.id, { source, source_key: selected?.needsBu ? bu : '' });
      onClose();
    } catch {} finally { setSaving(false); }
  };

  if (!options) return <div style={{ fontSize: '0.75rem', color: '#64748b', padding: '0.4rem' }}>Loading sources…</div>;

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.4rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.375rem' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#166534' }}>Actual comes from:</span>
      <select value={source} onChange={e => setSource(e.target.value)} style={{ ...st.input, width: '300px' }}>
        {!selected && <option value={source}>{source} (legacy)</option>}
        {Object.entries(groups).map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </optgroup>
        ))}
      </select>
      {selected?.needsBu && (
        <>
          <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#166534' }}>Business unit:</span>
          <select value={bu} onChange={e => setBu(e.target.value)} style={{ ...st.input, width: '220px' }}>
            <option value="">{selected.defaultBu ? `Default (${selected.defaultBu})` : 'Default (match by department name)'}</option>
            <option value="*">All business units</option>
            {businessUnits.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </>
      )}
      <button onClick={handleSave} disabled={saving} style={{ ...st.addBtn, padding: '0.25rem 0.6rem', fontSize: '0.7rem' }}>{saving ? '...' : 'Save'}</button>
      <button onClick={onClose} style={{ ...st.cancelBtn, padding: '0.25rem 0.6rem', fontSize: '0.7rem' }}>Cancel</button>
      {selected?.note && <span style={{ fontSize: '0.65rem', color: '#64748b', flexBasis: '100%' }}>{selected.note}</span>}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────
export default function WinTheDayPage() {
  const [weekStart, setWeekStart] = useState(getMonday(toDateStr(new Date())));
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddKpi, setShowAddKpi] = useState(false);
  const [budgetItems, setBudgetItems] = useState([]);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [sourceOptions, setSourceOptions] = useState(null);

  const today = toDateStr(new Date());

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/win-the-day/board/${weekStart}`);
      setBoard(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // Real budget-tab line items, so KPIs can be linked to a real dollar/target figure instead
  // of guessing department/line-name strings - fetched once we know which fiscal year the
  // current week falls in.
  useEffect(() => {
    if (!board?.fiscalYear) return;
    apiClient.get(`/forecast/budget-items/${board.fiscalYear}`)
      .then(res => setBudgetItems(res.data.items || []))
      .catch(() => {});
  }, [board?.fiscalYear]);

  const navigate = (dir) => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(toDateStr(d));
  };

  const goThisWeek = () => setWeekStart(getMonday(today));

  const saveEntry = async (kpi_id, entry_date, value) => {
    try {
      await apiClient.put('/win-the-day/entries', { kpi_id, entry_date, actual_value: value });
      await loadBoard();
    } catch {}
  };

  const addKpi = async (data) => {
    await apiClient.post('/win-the-day/kpis', data);
    await loadBoard();
  };

  const deleteKpi = async (id) => {
    if (!window.confirm('Delete this KPI?')) return;
    await apiClient.delete(`/win-the-day/kpis/${id}`);
    await loadBoard();
  };

  const updateKpiLink = async (id, data) => {
    await apiClient.put(`/win-the-day/kpis/${id}`, data);
    await loadBoard();
  };

  // Source = where this KPI's ACTUAL numbers come from (and for job-based sources, which
  // business unit) - separate from the budget link above.
  useEffect(() => {
    apiClient.get('/win-the-day/source-options')
      .then(res => setSourceOptions(res.data))
      .catch(() => {});
  }, []);

  const updateKpiSource = async (id, data) => {
    try {
      await apiClient.put(`/win-the-day/kpis/${id}`, data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    }
    await loadBoard();
  };

  const sourceLabel = (kpi) => {
    const label = sourceOptions?.sources?.find(s => s.value === kpi.source)?.label || kpi.source;
    const bu = kpi.source_key === '*' ? ' · all business units' : (kpi.source_key ? ` · ${kpi.source_key}` : '');
    return `Actual: ${label}${bu} - click to change`;
  };

  const departments = useMemo(() => {
    if (!board) return [];
    return board.groups.map(g => g.department);
  }, [board]);

  const weekDays = board?.weekDays || [];

  // Total columns: KPI name + (5 days * 2 cols each) + week(3) + mtd(3) + actions(1) = 1+10+3+3+1 = 18
  const totalCols = 1 + weekDays.length * 2 + 3 + 3 + 1;

  return (
    <div>
      <Header title="Win the Day" />
      <div style={st.page}>
        {error && <ErrorBanner message={error} />}

        {/* Controls */}
        <div style={st.controls}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate(-1)} style={st.navBtn}>&#9664;</button>
            <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1e293b', minWidth: '180px', textAlign: 'center' }}>
              {board ? formatWeekLabel(weekDays) : '...'}
            </span>
            <button onClick={() => navigate(1)} style={st.navBtn}>&#9654;</button>
            <button onClick={goThisWeek} style={st.todayBtn}>This Week</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {board && (
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {board.monthName} &middot; {board.workingDays} working days &middot; FY{board.fiscalYear}
              </span>
            )}
            <button onClick={() => setShowAddKpi(true)} style={st.addBtn}>+ Add KPI</button>
          </div>
        </div>

        {showAddKpi && <AddKpiForm onAdd={addKpi} onCancel={() => setShowAddKpi(false)} departments={departments} budgetItems={budgetItems} sourceOptions={sourceOptions} />}

        {/* Board */}
        {loading ? <LoadingSpinner /> : board && (
          <div style={st.tableCard}>
            <div style={{ overflowX: 'auto' }}>
              <table style={st.table}>
                <thead>
                  {/* Day headers */}
                  <tr>
                    <th style={{ ...st.th, minWidth: '160px', position: 'sticky', left: 0, zIndex: 3, background: '#f8fafc' }}>KPI</th>
                    {weekDays.map(date => {
                      const { dayName, dayNum } = formatDayHeader(date);
                      const isToday = date === today;
                      return (
                        <th key={date} colSpan={2} style={{
                          ...st.th, textAlign: 'center',
                          background: isToday ? '#ede9fe' : '#f8fafc',
                          borderLeft: '2px solid #e2e8f0',
                        }}>
                          <div style={{ fontWeight: isToday ? '800' : '600', color: isToday ? '#6366f1' : '#64748b', fontSize: '0.7rem' }}>
                            {dayName} {dayNum}
                          </div>
                          {isToday && <div style={{ fontSize: '0.5rem', color: '#6366f1', fontWeight: '700' }}>TODAY</div>}
                        </th>
                      );
                    })}
                    <th colSpan={3} style={{ ...st.th, textAlign: 'center', borderLeft: '2px solid #6366f1', background: '#f0f0ff' }}>Week Total</th>
                    <th colSpan={3} style={{ ...st.th, textAlign: 'center', borderLeft: '2px solid #10b981', background: '#ecfdf5' }}>MTD</th>
                    <th style={{ ...st.th, width: '30px' }}></th>
                  </tr>
                  {/* Sub-headers: Bdg / Act per day */}
                  <tr>
                    <th style={{ ...st.th, position: 'sticky', left: 0, zIndex: 3, background: '#f8fafc' }}></th>
                    {weekDays.map(date => {
                      const isToday = date === today;
                      return (
                        <React.Fragment key={date}>
                          <th style={{ ...st.th, fontSize: '0.55rem', color: '#7c3aed', background: isToday ? '#ede9fe' : '#faf5ff', borderLeft: '2px solid #e2e8f0' }}>Bdg</th>
                          <th style={{ ...st.th, fontSize: '0.55rem', color: '#16a34a', background: isToday ? '#ede9fe' : '#ecfdf5' }}>Act</th>
                        </React.Fragment>
                      );
                    })}
                    <th style={{ ...st.thSub, borderLeft: '2px solid #6366f1', background: '#f0f0ff', color: '#7c3aed' }}>Bdg</th>
                    <th style={{ ...st.thSub, background: '#f0f0ff', color: '#16a34a' }}>Act</th>
                    <th style={{ ...st.thSub, background: '#f0f0ff', color: '#64748b' }}>%</th>
                    <th style={{ ...st.thSub, borderLeft: '2px solid #10b981', background: '#ecfdf5', color: '#7c3aed' }}>Bdg</th>
                    <th style={{ ...st.thSub, background: '#ecfdf5', color: '#16a34a' }}>Act</th>
                    <th style={{ ...st.thSub, background: '#ecfdf5', color: '#64748b' }}>%</th>
                    <th style={{ ...st.th }}></th>
                  </tr>
                </thead>
                <tbody>
                  {board.groups.map(group => (
                    <React.Fragment key={group.department}>
                      {/* Department header */}
                      <tr>
                        <td colSpan={totalCols} style={{
                          padding: '0.5rem 0.75rem',
                          background: DEPT_COLORS[group.department] || '#475569',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '0.75rem',
                          letterSpacing: '0.03em',
                          borderBottom: '1px solid #e2e8f0',
                        }}>
                          {displayDept(group.department)}
                        </td>
                      </tr>
                      {/* KPI rows */}
                      {group.kpis.map((kpi, idx) => {
                        const isAuto = kpi.source && kpi.source !== 'manual';
                        const rowBg = idx % 2 !== 0 ? '#f8fafc' : '#fff';
                        return (
                        <React.Fragment key={kpi.id}>
                          <tr style={{ background: rowBg }}>
                            {/* KPI Name */}
                            <td style={{ ...st.td, fontWeight: '600', position: 'sticky', left: 0, background: rowBg, zIndex: 1, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                              <span>{kpi.name}</span>
                              <button
                                onClick={() => setEditingSourceId(editingSourceId === kpi.id ? null : kpi.id)}
                                title={isAuto ? sourceLabel(kpi) : 'Manual entry - click to pull this from data instead'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.6rem', marginLeft: '0.2rem', color: isAuto ? '#6366f1' : '#94a3b8', padding: 0 }}
                              >
                                {isAuto ? '⚡' : '✎'}
                              </button>
                              <span style={{ fontSize: '0.5rem', color: '#94a3b8', marginLeft: '0.15rem' }}>
                                {kpi.unit === 'currency' ? '$' : kpi.unit === 'percent' ? '%' : '#'}
                              </span>
                              <button
                                onClick={() => setEditingLinkId(editingLinkId === kpi.id ? null : kpi.id)}
                                title={kpi.budget_department ? `Linked to ${kpi.budget_department} / ${kpi.budget_line_name}` : 'No budget link - click to link'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.55rem', marginLeft: '0.25rem', color: kpi.budget_department ? '#16a34a' : '#f59e0b' }}
                              >
                                &#128279;
                              </button>
                            </td>
                            {/* Daily Budget / Actual columns */}
                            {weekDays.map(date => {
                              const dayData = kpi.days[date] || { budget: 0, actual: null };
                              const isToday = date === today;
                              return (
                                <React.Fragment key={date}>
                                  {/* Budget */}
                                  <td style={{
                                    ...st.td, fontSize: '0.65rem', color: '#7c3aed',
                                    borderLeft: '2px solid #e2e8f0',
                                    background: isToday ? '#faf5ff' : undefined,
                                  }}>
                                    {fmtVal(dayData.budget, kpi.unit)}
                                  </td>
                                  {/* Actual */}
                                  <td style={{
                                    ...st.td, fontSize: '0.7rem',
                                    background: isToday ? '#faf5ff' : (isAuto ? '#f0fdf4' : undefined),
                                    fontWeight: isToday ? '700' : '500',
                                    color: dayData.actual != null && dayData.actual > 0
                                      ? (dayData.budget > 0 && dayData.actual >= dayData.budget ? '#16a34a' : '#dc2626')
                                      : '#cbd5e1',
                                  }}>
                                    {isAuto ? (
                                      <span>{fmtVal(dayData.actual, kpi.unit)}</span>
                                    ) : (
                                      <EditCell value={dayData.actual} unit={kpi.unit} onSave={v => saveEntry(kpi.id, date, v)} />
                                    )}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                            {/* Week Total */}
                            <td style={{ ...st.td, fontSize: '0.65rem', color: '#7c3aed', borderLeft: '2px solid #6366f1', background: '#f8f7ff' }}>
                              {fmtVal(kpi.weekTotal.budget, kpi.unit)}
                            </td>
                            <td style={{ ...st.td, fontSize: '0.7rem', fontWeight: '600', background: '#f8f7ff',
                              color: kpi.weekTotal.actual > 0
                                ? (kpi.weekTotal.budget > 0 && kpi.weekTotal.actual >= kpi.weekTotal.budget ? '#16a34a' : '#dc2626')
                                : '#cbd5e1',
                            }}>
                              {fmtVal(kpi.weekTotal.actual, kpi.unit)}
                            </td>
                            <td style={{ ...st.td, fontSize: '0.65rem', fontWeight: '700', background: '#f8f7ff',
                              color: kpi.weekTotal.pct >= 100 ? '#16a34a' : kpi.weekTotal.pct > 0 ? '#f59e0b' : '#cbd5e1',
                            }}>
                              {kpi.weekTotal.pct > 0 ? kpi.weekTotal.pct + '%' : '—'}
                            </td>
                            {/* MTD */}
                            <td style={{ ...st.td, fontSize: '0.65rem', color: '#7c3aed', borderLeft: '2px solid #10b981', background: '#f0fdf4' }}>
                              {fmtFull(kpi.mtd.budget, kpi.unit)}
                            </td>
                            <td style={{ ...st.td, fontSize: '0.7rem', fontWeight: '600', background: '#f0fdf4',
                              color: kpi.mtd.actual > 0
                                ? (kpi.mtd.budget > 0 && kpi.mtd.actual >= kpi.mtd.budget ? '#16a34a' : '#dc2626')
                                : '#cbd5e1',
                            }}>
                              {fmtFull(kpi.mtd.actual, kpi.unit)}
                            </td>
                            <td style={{ ...st.td, fontSize: '0.65rem', fontWeight: '700', background: '#f0fdf4',
                              color: kpi.mtd.pct >= 100 ? '#16a34a' : kpi.mtd.pct > 0 ? '#f59e0b' : '#cbd5e1',
                            }}>
                              {kpi.mtd.pct > 0 ? kpi.mtd.pct + '%' : '—'}
                            </td>
                            {/* Delete */}
                            <td style={{ ...st.td, textAlign: 'center' }}>
                              <button onClick={() => deleteKpi(kpi.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.65rem' }} title="Delete">&#10005;</button>
                            </td>
                          </tr>
                          {editingSourceId === kpi.id && (
                            <tr>
                              <td colSpan={totalCols} style={{ padding: '0.4rem 0.5rem', background: '#f0fdf4' }}>
                                <EditSource
                                  kpi={kpi}
                                  options={sourceOptions}
                                  onSave={updateKpiSource}
                                  onClose={() => setEditingSourceId(null)}
                                />
                              </td>
                            </tr>
                          )}
                          {editingLinkId === kpi.id && (
                            <tr>
                              <td colSpan={totalCols} style={{ padding: '0.4rem 0.5rem', background: '#fffbeb' }}>
                                <EditBudgetLink
                                  kpi={kpi}
                                  budgetItems={budgetItems}
                                  onSave={updateKpiLink}
                                  onClose={() => setEditingLinkId(null)}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────
const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  controls: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem',
    background: '#fff', borderRadius: '0.75rem', padding: '0.75rem 1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  navBtn: {
    padding: '0.35rem 0.6rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', color: '#475569',
  },
  todayBtn: {
    padding: '0.35rem 0.75rem', background: '#6366f1', color: '#fff', border: 'none',
    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
  },
  addBtn: {
    padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
  },
  cancelBtn: {
    padding: '0.4rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569',
  },
  input: {
    padding: '0.4rem 0.625rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.8rem', outline: 'none', color: '#1e293b',
  },
  tableCard: {
    background: '#fff', borderRadius: '0.75rem', padding: '0.75rem 1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' },
  th: {
    padding: '0.4rem 0.35rem', background: '#f8fafc', color: '#64748b', fontWeight: '600',
    fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right',
    borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
  },
  thSub: {
    padding: '0.25rem 0.35rem', fontWeight: '600', fontSize: '0.55rem',
    textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right',
    borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.25rem 0.35rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b',
    verticalAlign: 'middle', textAlign: 'right', fontSize: '0.7rem',
  },
};
